// Keep in sync with customer/src/utils/scheduleForm.js (opening-hours subset + name/info for admin)

export const SCHEDULE_DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const DAY_LABELS = {
  monday: 'Mo.',
  tuesday: 'Di.',
  wednesday: 'Mi.',
  thursday: 'Do.',
  friday: 'Fr.',
  saturday: 'Sa.',
  sunday: 'So.',
};

const DEFAULT_TIMES = [{ start: '09:00', end: '18:00' }];

export function normalizeTimeToHHMM(time) {
  if (!time) return '09:00';
  const str = String(time);
  if (str.includes('T')) {
    const match = str.match(/T(\d{2}:\d{2})/);
    if (match) return match[1];
  }
  return str.slice(0, 5);
}

export function createEmptySchedule(defaultTimes = DEFAULT_TIMES) {
  const schedule = {};
  SCHEDULE_DAY_KEYS.forEach((key) => {
    schedule[key] = {
      active: false,
      name: '',
      info: '',
      times: JSON.parse(JSON.stringify(defaultTimes)),
    };
  });
  return schedule;
}

export function cloneTimes(times) {
  return JSON.parse(JSON.stringify(times || DEFAULT_TIMES));
}

export function propagateTimes(schedule, sourceKey, customizedDays = new Set()) {
  if (!schedule || !sourceKey || !schedule[sourceKey]) return;
  const sourceTimes = cloneTimes(schedule[sourceKey].times);

  SCHEDULE_DAY_KEYS.forEach((key) => {
    if (key === sourceKey) return;
    if (!schedule[key]?.active) return;
    if (customizedDays.has(key)) return;
    schedule[key].times = cloneTimes(sourceTimes);
  });
}

export function copyTimesToDay(schedule, targetKey, sourceKey) {
  if (!schedule?.[targetKey] || !schedule?.[sourceKey]) return;
  schedule[targetKey].times = cloneTimes(schedule[sourceKey].times);
}

export function activateDay(schedule, key, sourceKey) {
  if (!schedule[key]) return;
  schedule[key].active = true;
  if (sourceKey && schedule[sourceKey]) {
    copyTimesToDay(schedule, key, sourceKey);
  }
}

export function countActiveDays(schedule) {
  return SCHEDULE_DAY_KEYS.filter((key) => schedule[key]?.active).length;
}

export function firstActiveDayKey(schedule) {
  return SCHEDULE_DAY_KEYS.find((key) => schedule[key]?.active) ?? 'monday';
}

export function handleScheduleDayClick(schedule, activeDayKey, clickedKey, customizedDays) {
  const day = schedule[clickedKey];
  if (!day) return activeDayKey;

  if (day.active) {
    if (countActiveDays(schedule) <= 1) return activeDayKey;
    schedule[clickedKey] = { ...day, active: false };
    customizedDays.delete(clickedKey);
    if (activeDayKey === clickedKey) {
      return firstActiveDayKey(schedule);
    }
    return activeDayKey;
  }

  activateDay(schedule, clickedKey, activeDayKey);
  return clickedKey;
}

/** Opening hours: select days without copying times between them. */
export function handleOpeningHoursDayClick(schedule, activeDayKey, clickedKey) {
  const day = schedule[clickedKey];
  if (!day) return activeDayKey;

  if (day.active) {
    if (activeDayKey === clickedKey && countActiveDays(schedule) > 1) {
      schedule[clickedKey] = { ...day, active: false };
      return firstActiveDayKey(schedule);
    }
    return clickedKey;
  }

  activateDay(schedule, clickedKey);
  return clickedKey;
}

export function scheduleFromCompany(company) {
  const source = {};
  SCHEDULE_DAY_KEYS.forEach((key) => {
    source[key] = company?.[key];
  });
  return normalizeScheduleForm(source);
}

export function normalizeScheduleForm(schedule, defaultTimes = DEFAULT_TIMES) {
  const normalized = createEmptySchedule(defaultTimes);
  const source = schedule && typeof schedule === 'object' ? schedule : {};

  SCHEDULE_DAY_KEYS.forEach((key) => {
    const day = source[key];
    if (!day || typeof day !== 'object') return;

    const times = day.times?.length
      ? day.times.map((time) => ({
        start: normalizeTimeToHHMM(time.start),
        end: normalizeTimeToHHMM(time.end),
      }))
      : cloneTimes(normalized[key].times);

    normalized[key] = {
      active: !!day.active,
      name: day.name || '',
      info: day.info || '',
      times,
    };
  });

  return normalized;
}

export function applyScheduleToCompany(company, schedule) {
  const updated = { ...company };
  SCHEDULE_DAY_KEYS.forEach((key) => {
    const day = schedule[key];
    if (!day) return;
    updated[key] = {
      ...(updated[key] || {}),
      active: !!day.active,
      name: day.name || '',
      info: day.info || '',
      times: cloneTimes(day.times),
    };
  });
  return updated;
}

export function normalizeCompanyOpeningHours(company) {
  if (!company || typeof company !== 'object') return company;
  const schedule = scheduleFromCompany(company);
  return applyScheduleToCompany(company, schedule);
}

// --- Deal schedule (keep in sync with customer/src/utils/scheduleForm.js) ---

export function formatTimeForAPI(time) {
  if (!time) return '09:00:00';
  const str = String(time);
  return str.length >= 8 ? str : `${str}:00`;
}

export function createEmptyDealSchedule(defaultTimes = DEFAULT_TIMES) {
  const schedule = {};
  SCHEDULE_DAY_KEYS.forEach((key) => {
    schedule[key] = {
      active: false,
      times: JSON.parse(JSON.stringify(defaultTimes)),
      quantityLimit: null,
    };
  });
  return schedule;
}

export function handleDealDayClick(schedule, activeDayKey, clickedKey) {
  const day = schedule[clickedKey];
  if (!day) return activeDayKey;

  if (day.active) {
    if (activeDayKey === clickedKey && countActiveDays(schedule) > 1) {
      schedule[clickedKey] = { ...day, active: false };
      return firstActiveDayKey(schedule);
    }
    return clickedKey;
  }

  activateDay(schedule, clickedKey, activeDayKey);
  return clickedKey;
}

export function scheduleFromLegacy(validDays, timeStart, timeEnd, defaultTimes = DEFAULT_TIMES) {
  const schedule = createEmptyDealSchedule(defaultTimes);
  const days = validDays
    ? String(validDays).split(',').map(Number).filter(Boolean)
    : [1, 2, 3, 4, 5, 6, 7];

  const start = normalizeTimeToHHMM(timeStart || defaultTimes[0].start);
  const end = normalizeTimeToHHMM(timeEnd || defaultTimes[0].end);
  const times = [{ start, end }];

  SCHEDULE_DAY_KEYS.forEach((key, index) => {
    const isoDay = index + 1;
    schedule[key] = {
      active: days.includes(isoDay),
      times: cloneTimes(times),
      quantityLimit: null,
    };
  });

  return schedule;
}

export function legacyFromSchedule(schedule, isAllDay = false) {
  const activeDays = [];
  let timeStart = null;
  let timeEnd = null;

  SCHEDULE_DAY_KEYS.forEach((key, index) => {
    const day = schedule?.[key];
    if (!day?.active) return;

    activeDays.push(index + 1);

    if (!timeStart && day.times?.length) {
      timeStart = formatTimeForAPI(day.times[0].start);
      timeEnd = formatTimeForAPI(day.times[0].end);
    }
  });

  return {
    validDays: activeDays.length ? activeDays.join(',') : '',
    timeStart: isAllDay ? null : timeStart,
    timeEnd: isAllDay ? null : timeEnd,
  };
}

export function parseDaySchedule(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function normalizeDealScheduleForm(schedule, defaultTimes = DEFAULT_TIMES) {
  const normalized = createEmptyDealSchedule(defaultTimes);
  const source = schedule && typeof schedule === 'object' ? schedule : {};

  SCHEDULE_DAY_KEYS.forEach((key) => {
    const day = source[key];
    if (!day || typeof day !== 'object') return;

    const times = day.times?.length
      ? day.times.map((time) => ({
        start: normalizeTimeToHHMM(time.start),
        end: normalizeTimeToHHMM(time.end),
      }))
      : cloneTimes(normalized[key].times);

    normalized[key] = {
      active: !!day.active,
      times,
      quantityLimit: day.quantityLimit === null || day.quantityLimit === undefined || day.quantityLimit === ''
        ? null
        : Number(day.quantityLimit),
    };
  });

  return normalizeDealScheduleToSingleSlot(normalized);
}

const WEEKDAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const WEEKDAY_NAME_TO_KEY = {
  Monday: 'monday',
  Tuesday: 'tuesday',
  Wednesday: 'wednesday',
  Thursday: 'thursday',
  Friday: 'friday',
  Saturday: 'saturday',
  Sunday: 'sunday',
};

export { WEEKDAY_KEYS };

const resolveTimeZone = (timezone) => {
  if (!timezone) return 'Europe/Berlin';
  const value = String(timezone);
  if (value.includes('/')) return value;
  return 'Europe/Berlin';
};

const getCalendarPartsInTimeZone = (date, timeZone) => {
  const tz = resolveTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const pick = (type) => Number(parts.find((part) => part.type === type)?.value || 0);
  return { year: pick('year'), month: pick('month'), day: pick('day') };
};

const dateAtCalendarDayOffset = (year, month, day, dayOffset) => (
  new Date(Date.UTC(year, month - 1, day + dayOffset, 12, 0, 0))
);

export function getWeekdayKeyForDate(date = new Date(), timeZone = 'Europe/Berlin') {
  const name = new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTimeZone(timeZone),
    weekday: 'long',
  }).format(date);
  return WEEKDAY_NAME_TO_KEY[name] || 'monday';
}

export function findNextOpenDayKey(openingSchedule, fromDate = new Date(), timeZone = 'Europe/Berlin') {
  const tz = resolveTimeZone(timeZone);
  const { year, month, day } = getCalendarPartsInTimeZone(fromDate, tz);
  for (let offset = 0; offset < 7; offset += 1) {
    const date = dateAtCalendarDayOffset(year, month, day, offset);
    const key = getWeekdayKeyForDate(date, tz);
    const openDay = openingSchedule?.[key];
    if (openDay?.active && openDay.times?.length) return key;
  }
  return null;
}

const minutesToHHMM = (minutes) => {
  const total = ((minutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export function mergeTimesToSingleSpan(times) {
  if (!times?.length) return cloneTimes(DEFAULT_TIMES);
  if (times.length === 1) {
    return [{
      start: normalizeTimeToHHMM(times[0].start),
      end: normalizeTimeToHHMM(times[0].end),
    }];
  }

  let minStart = Infinity;
  let maxEnd = -Infinity;

  times.forEach((slot) => {
    if (!slot?.start || !slot?.end) return;
    let startMin = timeToMinutes(slot.start);
    let endMin = timeToMinutes(slot.end);
    if (endMin <= startMin) {
      endMin += 24 * 60;
    }
    minStart = Math.min(minStart, startMin);
    maxEnd = Math.max(maxEnd, endMin);
  });

  if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd)) {
    return cloneTimes(DEFAULT_TIMES);
  }

  return [{
    start: minutesToHHMM(minStart),
    end: minutesToHHMM(maxEnd >= 24 * 60 ? maxEnd % (24 * 60) : maxEnd),
  }];
}

export function normalizeDealScheduleToSingleSlot(schedule) {
  if (!schedule) return schedule;
  SCHEDULE_DAY_KEYS.forEach((key) => {
    const day = schedule[key];
    if (!day?.times?.length) return;
    day.times = mergeTimesToSingleSpan(day.times);
  });
  return schedule;
}

export function isAllDayFlag(value) {
  return value === true || value === 1 || value === '1';
}

export function dealScheduleFromOpeningHours(company) {
  const opening = scheduleFromCompany(company || {});
  const deal = createEmptyDealSchedule();

  SCHEDULE_DAY_KEYS.forEach((key) => {
    const day = opening[key];
    if (day?.active && day.times?.length) {
      deal[key] = {
        active: true,
        times: mergeTimesToSingleSpan(day.times),
        quantityLimit: null,
      };
    }
  });

  if (countActiveDays(deal) === 0) {
    WEEKDAY_KEYS.forEach((key) => {
      activateDay(deal, key);
    });
  }

  return deal;
}

export function dealScheduleFromOpeningHoursOnce(company, now = new Date()) {
  const opening = scheduleFromCompany(company || {});
  const timeZone = resolveTimeZone(company?.timezone);
  const dayKey = findNextOpenDayKey(opening, now, timeZone);
  return dealScheduleFromOpeningHoursOnceForDay(company, dayKey);
}

export function getActiveDealScheduleDayKey(dealSchedule) {
  return SCHEDULE_DAY_KEYS.find((key) => dealSchedule?.[key]?.active) || null;
}

export function dealScheduleFromOpeningHoursOnceForDay(company, dayKey) {
  const opening = scheduleFromCompany(company || {});
  const deal = createEmptyDealSchedule();

  if (dayKey && opening[dayKey]?.active && opening[dayKey]?.times?.length) {
    deal[dayKey] = {
      active: true,
      times: mergeTimesToSingleSpan(opening[dayKey].times),
      quantityLimit: null,
    };
    return deal;
  }

  WEEKDAY_KEYS.forEach((key) => {
    activateDay(deal, key);
  });
  return deal;
}

const normalizeDealTimesForCompare = (times = []) => JSON.stringify(
  times.map((time) => ({
    start: normalizeTimeToHHMM(time.start),
    end: normalizeTimeToHHMM(time.end),
  })),
);

export function dealScheduleContentEqual(a, b) {
  return SCHEDULE_DAY_KEYS.every((key) => {
    const dayA = a?.[key] || {};
    const dayB = b?.[key] || {};
    if (!!dayA.active !== !!dayB.active) return false;
    if (!dayA.active) return true;
    return normalizeDealTimesForCompare(dayA.times) === normalizeDealTimesForCompare(dayB.times);
  });
}

export function isDealScheduleMatchingOpeningHours(dealSchedule, company, scheduleType = 'WEEKLY') {
  if (scheduleType === 'ONCE') {
    const savedDayKey = getActiveDealScheduleDayKey(dealSchedule);
    if (savedDayKey) {
      const expected = dealScheduleFromOpeningHoursOnceForDay(company, savedDayKey);
      return dealScheduleContentEqual(dealSchedule, expected);
    }
  }

  const expected = scheduleType === 'ONCE'
    ? dealScheduleFromOpeningHoursOnce(company)
    : dealScheduleFromOpeningHours(company);
  return dealScheduleContentEqual(dealSchedule, expected);
}

export function timeToMinutes(time) {
  const normalized = normalizeTimeToHHMM(time);
  const [hours, minutes] = normalized.split(':').map(Number);
  return hours * 60 + (minutes || 0);
}

export function isTimeRangeWithinOpening(dealStart, dealEnd, openingSlots) {
  if (!openingSlots?.length) return false;

  let dealStartMinutes = timeToMinutes(dealStart);
  let dealEndMinutes = timeToMinutes(dealEnd);
  if (dealEndMinutes <= dealStartMinutes) {
    dealEndMinutes += 24 * 60;
  }

  return openingSlots.some((slot) => {
    let openStart = timeToMinutes(slot.start);
    let openEnd = timeToMinutes(slot.end);
    if (openEnd <= openStart) {
      openEnd += 24 * 60;
    }
    return dealStartMinutes >= openStart && dealEndMinutes <= openEnd;
  });
}

export function getDealScheduleOpeningWarnings(dealSchedule, openingSchedule) {
  const warnings = [];
  if (!dealSchedule || !openingSchedule) return warnings;

  SCHEDULE_DAY_KEYS.forEach((key) => {
    const dealDay = dealSchedule[key];
    if (!dealDay?.active) return;

    const openDay = openingSchedule[key];
    const dayLabel = DAY_LABELS[key] || key;

    if (!openDay?.active || !openDay.times?.length) {
      warnings.push(`${dayLabel}: Deal aktiv, Lokal an diesem Tag geschlossen.`);
      return;
    }

    (dealDay.times || []).forEach((slot) => {
      if (!slot?.start || !slot?.end) return;
      if (!isTimeRangeWithinOpening(slot.start, slot.end, openDay.times)) {
        warnings.push(
          `${dayLabel} ${normalizeTimeToHHMM(slot.start)}–${normalizeTimeToHHMM(slot.end)}: außerhalb der Öffnungszeiten.`,
        );
      }
    });
  });

  return warnings;
}
